/* eslint-disable max-lines */
import {
  OnResumeSuccessHandler,
  OnTokenizeSuccessHandler,
} from '@primer-io/checkout-web'
import i18n from 'i18next'
import { primerPaymentApi, userApi } from '~/shared/api'
import {
  PRIMER_PAYMENT_ERRORS,
  PaymentMethod,
} from '~/shared/constants/paymentConstants'
import { getPaymentProcessorName } from '~/shared/helpers/getPaymentProcessorName'
import { getPurchaseFailedEventParams } from '~/shared/helpers/getPurchaseFailedEventParams'
import { getPurchaseSuccessEventParams } from '~/shared/helpers/getPurchaseSuccessEventParams'
import { getSubscriptionProductId } from '~/shared/helpers/getSubscriptionProductId'
import { logGeneralAddToCartEvents } from '~/shared/helpers/logGeneralAddToCartEvents'
import { logSuccessfulPayment } from '~/shared/helpers/logSuccessfulPayment'
import { eventLogger } from '~/shared/services/eventLogger'
import { tiktokPixelService } from '~/shared/services/tiktokPixelService'
import {
  setErrorAction,
  startFetching,
  stopFetching,
} from '~/shared/store/common/actions'
import { selectCohortID } from '~/shared/store/common/selects'
import {
  selectSelectedSubscription,
  selectSelectedSubscriptionCurrency,
  selectSelectedSubscriptionFullPrice,
  selectSelectedSubscriptionLookupKey,
  selectSelectedSubscriptionPeriodName,
  selectSelectedSubscriptionPeriodQuantity,
  selectSelectedSubscriptionPlanID,
  selectSelectedSubscriptionTrialDays,
  selectSelectedSubscriptionTrialPrice,
} from '~/shared/store/subscription/selects'
import { IAppState, TAppDispatchThunk } from '~/shared/store/types'
import { sendUserConfigAction } from '~/shared/store/user/actions/asyncActions'
import { updateUserConfigAction } from '~/shared/store/user/actions/syncActions'
import {
  selectUserPaymentId,
  selectUserPaymentMethod,
  selectUserPaymentSystem,
  selectUserUUID,
} from '~/shared/store/user/selects'

import {
  GET_PRIMER_CLIENT_SESSION_TOKEN,
  PRIMER_PURCHASE_PROCESSING,
  PURCHASE,
  UPDATE_PRIMER_CLIENT_SESSION,
} from '../actionTypes'
import {
  selectIsForce3DS,
  selectIsPrimerFirstPaymentRetryPassed,
  selectPrimerClientToken,
} from '../selects'
import {
  setIsAlreadyBoughtAction,
  setIsForce3DSAction,
  setIsPrimerRetryProcessing,
  setIsSubscriptionPurchaseInitiated,
  setPaymentProcessorNamesAction,
  setPrimerClientTokenAction,
} from './syncActions'

export function updatePrimerClientSessionAction(
  retryCallback?: () => void,
): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUserUUID(state)
    const clientToken = selectPrimerClientToken(state)
    const paymentId = selectUserPaymentId(state)

    dispatch(startFetching(UPDATE_PRIMER_CLIENT_SESSION))

    try {
      const response = await primerPaymentApi.updateClientSession({
        uuid,
        paymentId,
        clientToken,
      })

      dispatch(stopFetching(UPDATE_PRIMER_CLIENT_SESSION))

      if (response.success && response.data && response.data.should_retry) {
        dispatch(stopFetching(PURCHASE))
        dispatch(setPrimerClientTokenAction(response.data.client_token))
        dispatch(setIsPrimerRetryProcessing(true))
        retryCallback?.()

        return
      }

      const error = i18n.t(
        PRIMER_PAYMENT_ERRORS[response.data.payment_error?.toUpperCase()] ||
          PRIMER_PAYMENT_ERRORS.COMMON_ERROR,
      )

      dispatch(setIsPrimerRetryProcessing(false))
      eventLogger.logPaymentDeclined('primer_update_session_no_retry')
      dispatch(setErrorAction(error))
      dispatch(stopFetching(UPDATE_PRIMER_CLIENT_SESSION))
      dispatch(stopFetching(PURCHASE))
    } catch (error: any) {
      dispatch(setIsPrimerRetryProcessing(false))
      eventLogger.logPaymentDeclined('primer_update_session_catch')
      dispatch(setErrorAction(error.toString()))
      dispatch(stopFetching(UPDATE_PRIMER_CLIENT_SESSION))
      dispatch(stopFetching(PURCHASE))
    }
  }
}

export function getPrimerClientSessionTokenAction(): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUserUUID(state)
    const planId = selectSelectedSubscriptionPlanID(state)
    const trialPeriodDays = selectSelectedSubscriptionTrialDays(state)
    const cohort = selectCohortID(state)

    dispatch(startFetching(GET_PRIMER_CLIENT_SESSION_TOKEN))

    const response = await primerPaymentApi.getClientSessionToken({
      uuid,
      cohort,
      planId,
      trialPeriodDays,
    })

    // The user may have selected a different plan while this request was in
    // flight — a newer request already owns the result in that case, so this
    // (now stale) response must not overwrite it.
    const isStale = selectSelectedSubscriptionPlanID(getState()) !== planId

    if (!isStale) {
      if (response.success && response.data) {
        dispatch(setPrimerClientTokenAction(response.data.client_token))
        dispatch(setIsForce3DSAction(response.data['3ds_force'] ?? false))
        dispatch(
          setPaymentProcessorNamesAction({
            processorName: response.data.processor_name ?? '',
            applePayProcessorName: response.data.apple_pay_processor_name ?? '',
            googlePayProcessorName:
              response.data.google_pay_processor_name ?? '',
            paypalProcessorName: response.data.paypal_processor_name ?? '',
          }),
        )
      }

      if (!response.success && response.data) {
        if (response.data.error === 'user already bought a subscription') {
          dispatch(setIsAlreadyBoughtAction(true))
        } else {
          eventLogger.logPaymentDeclined('primer_get_client_token')
          dispatch(setErrorAction(response.data.error))
        }
      }
    }

    dispatch(stopFetching(GET_PRIMER_CLIENT_SESSION_TOKEN))
  }
}

export function primerConfirmPaymentAction(
  paymentId: string,
  handler: OnTokenizeSuccessHandler,
): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUserUUID(state)
    const paymentMethod = selectUserPaymentMethod(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const isForce3DS = selectIsForce3DS(state)
    const paymentProcessor = getPaymentProcessorName(state, paymentMethod)

    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    try {
      const response = await primerPaymentApi.confirmSubscription({
        uuid,
        paymentId,
      })

      if (!response.data && !window.navigator.onLine) {
        eventLogger.logPaymentDeclined('primer_confirm_connection_error')
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.CONNECTION_ERROR)))
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.success && response?.data?.error) {
        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          paymentMethod,
          error: response?.data?.error,
          paymentSystem,
          isForce3DS,
          paymentProcessor,
        })
        eventLogger.logPaymentDeclined('primer_confirm_payment_failed')
        dispatch(setErrorAction(response?.data?.error))
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))

        handler.handleFailure(response?.data?.error)
        return
      }

      try {
        handler.handleSuccess()
      } catch (handlerError: any) {
        eventLogger.logPrimerHandlerSuccessFailed(handlerError?.toString())
        console.error('handler.handleSuccess failed:', handlerError)
      }
    } catch (error: any) {
      const stringError = error.toString()
      eventLogger.logPurchaseFailed({
        ...getPurchaseFailedEventParams(state),
        paymentMethod,
        paymentSystem,
        error: { type: 'error', description: stringError },
        isForce3DS,
        paymentProcessor,
      })
      eventLogger.logPaymentDeclined('primer_confirm_catch')
      dispatch(setErrorAction(stringError))
      handler.handleFailure(stringError)
    } finally {
      dispatch(stopFetching(PURCHASE))
      dispatch(stopFetching(PRIMER_PURCHASE_PROCESSING))
      dispatch(setIsPrimerRetryProcessing(false))
    }
  }
}

export function primerPurchaseAction(
  token: string,
  handler: OnTokenizeSuccessHandler,
  retryPaymentCallback: () => void,
): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUserUUID(state)
    const paymentMethod = selectUserPaymentMethod(state)
    const priceId = selectSelectedSubscriptionLookupKey(state)
    const trialPeriodDays = selectSelectedSubscriptionTrialDays(state)
    const fullPrice = selectSelectedSubscriptionFullPrice(state)
    const selectedSubscription = selectSelectedSubscription(state)
    const trialPrice = selectSelectedSubscriptionTrialPrice(state)
    const currency = selectSelectedSubscriptionCurrency(state)
    const periodName = selectSelectedSubscriptionPeriodName(state)
    const periodQuantity = selectSelectedSubscriptionPeriodQuantity(state)
    const discountTried = selectIsPrimerFirstPaymentRetryPassed(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const isForce3DS = selectIsForce3DS(state)
    const paymentProcessor = getPaymentProcessorName(state, paymentMethod)
    const purchaseSuccessParams = getPurchaseSuccessEventParams(state)

    dispatch(setIsSubscriptionPurchaseInitiated(true))
    dispatch(
      sendUserConfigAction({
        payment_currency: currency,
        payment_method: paymentMethod,
        payment_system: paymentSystem,
        is_download_visited: false,
        subscription_price: `${fullPrice}`,
        subscription_duration: `${periodQuantity}${periodName}`,
        price_id: priceId,
        screen_name: purchaseSuccessParams.screenName,
        product_name: purchaseSuccessParams.productId,
        period_quantity: periodQuantity,
        trial_price: `${trialPrice}`,
        trial_period: `${trialPeriodDays}`,
      }),
    )

    dispatch(startFetching(PURCHASE))

    logGeneralAddToCartEvents({
      uuid,
      selectedSubscription,
      currency: currency.toUpperCase(),
      price: trialPrice || fullPrice,
    })

    if (selectedSubscription) {
      tiktokPixelService.trackInitiateCheckout({
        value: trialPrice || fullPrice,
        currency,
        contentId: getSubscriptionProductId(selectedSubscription),
        eventId: uuid,
      })
    }

    try {
      const response = await primerPaymentApi.createSubscription({
        uuid,
        token,
      })

      if (!response.data && !window.navigator.onLine) {
        eventLogger.logPaymentDeclined('primer_purchase_connection_error')
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.CONNECTION_ERROR)))
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.data.payment.is_success) {
        const error = {
          type: response.data.payment.status_reason.type,
          description: response.data.payment.status_reason.message,
          code: response.data.payment.status_reason.code,
        }

        eventLogger.logPurchaseFailed({
          ...getPurchaseFailedEventParams(state),
          paymentMethod,
          error,
          paymentSystem,
          isForce3DS,
          paymentProcessor,
        })

        handler.handleFailure(error.description)

        eventLogger.logPaymentDeclined('primer_purchase_is_success_false')
        dispatch(
          setErrorAction(
            i18n.t(
              PRIMER_PAYMENT_ERRORS[error.code?.toUpperCase()] ||
                PRIMER_PAYMENT_ERRORS.COMMON_ERROR,
            ),
          ),
        )
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))

        if (paymentMethod === PaymentMethod.CREDIT_CARD && !discountTried) {
          dispatch(
            updateUserConfigAction({
              paymentId: response.data.payment.payment_id,
            }),
          )
          dispatch(updatePrimerClientSessionAction(retryPaymentCallback))
        }

        return
      }

      if (response.data.payment.client_token) {
        dispatch(setPrimerClientTokenAction(response.data.payment.client_token))
        dispatch(
          updateUserConfigAction({
            paymentId: response.data.payment.payment_id,
          }),
        )

        handler.continueWithNewClientToken(response.data.payment.client_token)
        return
      }

      dispatch(
        primerConfirmPaymentAction(response.data.payment.payment_id, handler),
      )

      try {
        const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

        logSuccessfulPayment({
          ...purchaseSuccessParams,
          price: fullPrice,
          trialPrice,
          trialPeriodDays,
          discountApplied: response.data.purchase.discount_applied,
          subscriptionId: response.data.purchase.subscription_id,
          paymentMethod,
          paymentSystem,
          productId: response.data.purchase.product_id,
          predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
          isForce3DS,
          paymentProcessor,
        })

        dispatch(
          sendUserConfigAction({
            discount_applied: !!response.data.purchase.discount_applied,
            payment_currency: currency,
            payment_method: paymentMethod,
            payment_system: paymentSystem,
            is_download_visited: false,
            subscription_price: `${fullPrice}`,
            subscription_duration: `${periodQuantity}${periodName}`,
            price_id: priceId,
            screen_name: purchaseSuccessParams.screenName,
            product_name: purchaseSuccessParams.productId,
            period_quantity: periodQuantity,
            trial_price: `${trialPrice}`,
            trial_period: `${trialPeriodDays}`,
          }),
        )
      } catch (loggingError: any) {
        console.error('Post-payment logging failed:', loggingError)
      }
    } catch (error: any) {
      const stringError = error.toString()
      eventLogger.logPurchaseFailed({
        ...getPurchaseFailedEventParams(state),
        paymentMethod,
        paymentSystem,
        error: { type: 'error', description: stringError },
        isForce3DS,
        paymentProcessor,
      })
      eventLogger.logPaymentDeclined('primer_purchase_catch')
      dispatch(setErrorAction(stringError))
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(stopFetching(PURCHASE))
      handler.handleFailure(stringError)
    }
  }
}

export function primerResumePurchaseAction(
  currentPaymentId: string | undefined,
  resumeToken: string,
  handler: OnResumeSuccessHandler,
  retryPaymentCallback: () => void,
): any {
  return async (
    dispatch: TAppDispatchThunk<any>,
    getState: () => IAppState,
  ) => {
    const state = getState()
    const uuid = selectUserUUID(state)
    const paymentMethod = selectUserPaymentMethod(state)
    const trialCurrentPrice = selectSelectedSubscriptionTrialPrice(state)
    const trialPeriodDays = selectSelectedSubscriptionTrialDays(state)
    const fullPrice = selectSelectedSubscriptionFullPrice(state)
    const currency = selectSelectedSubscriptionCurrency(state)
    const periodName = selectSelectedSubscriptionPeriodName(state)
    const periodQuantity = selectSelectedSubscriptionPeriodQuantity(state)
    const paymentId = selectUserPaymentId(state)
    const priceId = selectSelectedSubscriptionLookupKey(state)
    const discountTried = selectIsPrimerFirstPaymentRetryPassed(state)
    const paymentSystem = selectUserPaymentSystem(state)
    const isForce3DS = selectIsForce3DS(state)
    const paymentProcessor = getPaymentProcessorName(state, paymentMethod)

    const purchaseSuccessParams = getPurchaseSuccessEventParams(state)
    const commonPurchaseFailedParams = getPurchaseFailedEventParams(state)

    dispatch(
      sendUserConfigAction({
        payment_currency: currency,
        payment_method: paymentMethod,
        payment_system: paymentSystem,
        is_download_visited: false,
        subscription_price: `${fullPrice}`,
        subscription_duration: `${periodQuantity}${periodName}`,
        price_id: priceId,
        screen_name: purchaseSuccessParams.screenName,
        product_name: purchaseSuccessParams.productId,
        period_quantity: periodQuantity,
        trial_price: `${trialCurrentPrice}`,
        trial_period: `${trialPeriodDays}`,
      }),
    )

    dispatch(startFetching(PURCHASE))

    try {
      const response = await primerPaymentApi.resumeSubscription({
        uuid,
        paymentId: currentPaymentId || paymentId,
        resumeToken,
      })

      if (!response.data && !window.navigator.onLine) {
        eventLogger.logPaymentDeclined('primer_resume_connection_error')
        dispatch(setErrorAction(i18n.t(PRIMER_PAYMENT_ERRORS.CONNECTION_ERROR)))
        dispatch(stopFetching(PURCHASE))
        dispatch(setIsPrimerRetryProcessing(false))
        return
      }

      if (!response.data.payment.is_success) {
        const error = {
          type: response.data.payment.status_reason.type,
          description: response.data.payment.status_reason.message,
          code: response.data.payment.status_reason.code,
        }

        eventLogger.logPurchaseFailed({
          ...commonPurchaseFailedParams,
          paymentMethod,
          error,
          paymentSystem,
          isForce3DS,
          paymentProcessor,
        })

        handler.handleFailure(error.description)

        eventLogger.logPaymentDeclined('primer_resume_is_success_false')
        dispatch(
          setErrorAction(
            i18n.t(
              PRIMER_PAYMENT_ERRORS[error.code?.toUpperCase()] ||
                PRIMER_PAYMENT_ERRORS.COMMON_ERROR,
            ),
          ),
        )

        if (paymentMethod === PaymentMethod.CREDIT_CARD && !discountTried) {
          dispatch(updatePrimerClientSessionAction(retryPaymentCallback))
        } else {
          dispatch(stopFetching(PURCHASE))
          dispatch(setIsPrimerRetryProcessing(false))
        }

        return
      }

      if (response.data.payment.client_token) {
        dispatch(setPrimerClientTokenAction(response.data.payment.client_token))
        handler.continueWithNewClientToken(response.data.payment.client_token)
        return
      }

      dispatch(
        primerConfirmPaymentAction(response.data.payment.payment_id, handler),
      )

      try {
        const predictedLtvResponse = await userApi.getPredictedLtv(uuid)

        logSuccessfulPayment({
          ...purchaseSuccessParams,
          price: fullPrice,
          trialPrice: trialCurrentPrice,
          trialPeriodDays,
          discountApplied: response.data.purchase.discount_applied,
          subscriptionId: response.data.purchase.subscription_id,
          paymentMethod,
          paymentSystem,
          productId: response.data.purchase.product_id,
          predictedLtv: predictedLtvResponse?.data?.predicted_ltv,
          isForce3DS,
          paymentProcessor,
        })

        dispatch(
          sendUserConfigAction({
            discount_applied: !!response.data.purchase.discount_applied,
          }),
        )
      } catch (loggingError: any) {
        console.error('Post-payment logging failed:', loggingError)
      }
    } catch (error: any) {
      const stringError = error.toString()
      eventLogger.logPurchaseFailed({
        ...getPurchaseFailedEventParams(state),
        paymentMethod,
        paymentSystem,
        error: { type: 'error', description: stringError },
        isForce3DS,
        paymentProcessor,
      })
      eventLogger.logPaymentDeclined('primer_resume_catch')
      dispatch(setErrorAction(stringError))
      dispatch(setIsPrimerRetryProcessing(false))
      dispatch(stopFetching(PURCHASE))
      handler.handleFailure(stringError)
    } finally {
      dispatch(stopFetching(PRIMER_PURCHASE_PROCESSING))
    }
  }
}
